import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Thermometer, MapPin, Shield, Bell, FileText, QrCode,
  ArrowRight, CheckCircle, Truck, Clock, Wifi
} from 'lucide-react';

const stats = [
  { value: '99.9%', label: 'Compliance Rate' },
  { value: '2-8°C', label: 'Cold Chain Range' },
  { value: '24/7', label: 'Real-time Monitoring' },
  { value: '<5min', label: 'Alert Response' },
];

const features = [
  {
    icon: Thermometer,
    title: 'Temperature Monitoring',
    description: 'Continuous 2-8°C cold chain monitoring with instant breach alerts'
  },
  {
    icon: MapPin,
    title: 'GPS Tracking',
    description: 'Real-time location tracking with route optimization'
  },
  {
    icon: Shield,
    title: 'Tamper Detection',
    description: 'Bag open/close sensors and weight monitoring'
  },
  {
    icon: FileText,
    title: 'Compliance Reports',
    description: 'GDP-compliant documentation and audit trails'
  },
  {
    icon: QrCode,
    title: 'QR Verification',
    description: 'Public delivery verification for receivers'
  },
  {
    icon: Bell,
    title: 'Smart Alerts',
    description: 'Instant notifications for critical events'
  }
];

const benefits = [
  'GDP & WHO Compliance Ready',
  'Real-time Temperature Logs',
  'Automated Breach Detection',
  'Digital Delivery Verification',
  'Comprehensive Audit Trail',
  'Multi-tenant Platform'
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-red-50" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-200 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge 
                className="mb-6 bg-blue-100 text-blue-700 border-blue-200 font-medium"
                data-testid="hero-badge"
              >
                COLD CHAIN COMPLIANCE PLATFORM
              </Badge>
              
              <h1 
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
                data-testid="hero-title"
              >
                Track<span className="text-red-500">0</span>tic
                <span className="block text-primary text-3xl md:text-4xl mt-2">Secure Pharma Delivery</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                Real-time IoT monitoring for pharmaceutical logistics. Ensure temperature compliance, 
                track deliveries, and generate audit-ready reports for vaccines, biologics, and 
                temperature-sensitive medications.
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-lg"
                    data-testid="hero-cta-primary"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary h-14 px-8 rounded-lg"
                    data-testid="hero-cta-secondary"
                  >
                    Explore Features
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main Dashboard Preview */}
                <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <Truck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        Track<span className="text-red-500">0</span>tic Dashboard
                      </div>
                      <div className="text-xs text-gray-500">Live Monitoring</div>
                    </div>
                    <Badge className="ml-auto bg-green-100 text-green-700">Active</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <Thermometer className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-blue-600">4.2°C</div>
                      <div className="text-xs text-gray-600">Current Temp</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-green-600">100%</div>
                      <div className="text-xs text-gray-600">Compliance</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Trip Progress</span>
                      <span className="text-sm text-primary font-semibold">75%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: '75%' }}></div>
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>Warehouse</span>
                      <span>3 of 4 deliveries</span>
                      <span>Return</span>
                    </div>
                  </div>
                </div>

                {/* Floating Cards */}
                <motion.div 
                  className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border border-gray-100"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-gray-900">Delivery Verified</div>
                      <div className="text-gray-500">2 mins ago</div>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-gray-100"
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 3, delay: 1.5, repeat: Infinity }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Wifi className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-gray-900">Device Online</div>
                      <div className="text-gray-500">Signal Strong</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              PLATFORM FEATURES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for
              <span className="text-primary"> Cold Chain Compliance</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A complete solution for pharmaceutical logistics monitoring, from warehouse to delivery.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/features">
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
              >
                View All Features
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                WHY TRACK0TIC
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Built for Pharmaceutical
                <span className="block">Compliance Standards</span>
              </h2>
              <p className="text-blue-100 leading-relaxed mb-8">
                Track<span className="text-red-300">0</span>tic is designed specifically for the pharmaceutical industry, 
                ensuring your deliveries meet GDP, WHO, and D&C Act requirements.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                    <span className="text-white text-sm">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center">
                  <div className="text-6xl font-bold text-white mb-2">99.9%</div>
                  <div className="text-blue-200 mb-6">Compliance Rate</div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <Clock className="w-6 h-6 text-white mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">24/7</div>
                      <div className="text-xs text-blue-200">Monitoring</div>
                    </div>
                    <div>
                      <Bell className="w-6 h-6 text-white mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">&lt;5m</div>
                      <div className="text-xs text-blue-200">Alert Time</div>
                    </div>
                    <div>
                      <FileText className="w-6 h-6 text-white mx-auto mb-2" />
                      <div className="text-2xl font-bold text-white">100%</div>
                      <div className="text-xs text-blue-200">Audit Ready</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Secure Your
              <span className="text-primary"> Pharmaceutical Deliveries?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us for a demo and learn how Track<span className="text-red-500">0</span>tic can 
              transform your cold chain logistics with real-time monitoring and compliance automation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-lg"
                  data-testid="cta-contact-btn"
                >
                  Request a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/ops/login">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary h-14 px-8 rounded-lg"
                  data-testid="cta-login-btn"
                >
                  Login to Platform
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
